import { PipeService } from 'src/pages/Pipe/Pipe.service.ts';
import type { tTagFilter } from 'src/pages/Pipe/Pipe.types.ts';

// The workspace tag catalog is read by the typeahead and changed by the attach
// and delete callbacks in ChatInfoAttributes.hook.ts. Neither side owns it, so
// the cache lives here rather than inside the typeahead's component file: the
// hook cannot reach a cache held in a `.component.tsx` module without either
// importing a component for a side effect or duplicating the request.
const tagsByWorkspace = new Map<string, Promise<tTagFilter[]>>();

// Bumped on every invalidation so a mounted typeahead can notice that the
// catalog it is showing is no longer the one this module would hand out.
let revision = 0;
const listeners = new Set<() => void>();

export const getWorkspaceTags = (
  workspaceId: string,
): Promise<tTagFilter[]> => {
  const cachedTags = tagsByWorkspace.get(workspaceId);
  if (cachedTags) return cachedTags;

  const request = PipeService.getTags(workspaceId).then(
    (response) => response.data ?? [],
  );
  tagsByWorkspace.set(workspaceId, request);
  void request.catch(() => {
    // An invalidated request must not evict a newer catalog request.
    if (tagsByWorkspace.get(workspaceId) === request) {
      tagsByWorkspace.delete(workspaceId);
    }
  });
  return request;
};

/**
 * Refresh after a successful attach or delete instead of reusing the page-load
 * snapshot. Otherwise a newly created tag can still be offered for creation
 * after it is detached without leaving the chat (BUG-940).
 */
export const invalidateWorkspaceTags = (workspaceId: string): void => {
  tagsByWorkspace.delete(workspaceId);
  revision += 1;
  listeners.forEach((listener) => listener());
};

export const subscribeToTagCatalog = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getTagCatalogRevision = (): number => revision;
